import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { db } from "../firebase";
import { ref, get, set, update } from "firebase/database";

const MASTER_USER = "jauhari";
const MASTER_PASS = "9336611232";
const MAX_LOGIN_ATTEMPTS = 5;
const LOCKOUT_TIME = 15 * 60 * 1000; // 15 minutes

export default function LoginPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [msgType, setMsgType] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [lockoutTimeLeft, setLockoutTimeLeft] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const loginAttemptsRef = useRef({});

  // Check for saved credentials on mount
  useEffect(() => {
    const savedUsername = localStorage.getItem("savedUsername");
    const savedPassword = localStorage.getItem("savedPassword");
    const remember = localStorage.getItem("rememberMe");
    const lastArea = localStorage.getItem("lastArea");
    
    if (remember === "true" && savedUsername && savedPassword) {
      setUsername(savedUsername);
      setPassword(savedPassword);
      setRememberMe(true);
    }
    
    // Check if there's a redirect path
    const from = location.state?.from?.pathname;
    if (from && from !== "/login") {
      sessionStorage.setItem("redirectAfterLogin", from);
    }
    
    // Clear any stale sessions
    clearStaleSessions();
  }, [location]);

  // Lockout timer effect
  useEffect(() => {
    let interval;
    if (isLocked && lockoutTimeLeft > 0) {
      interval = setInterval(() => {
        setLockoutTimeLeft(prev => {
          if (prev <= 1000) {
            setIsLocked(false);
            setFailedAttempts(0);
            return 0;
          }
          return prev - 1000;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isLocked, lockoutTimeLeft]);

  const clearStaleSessions = async () => {
    try {
      const sessionsRef = ref(db, "sessions");
      const snapshot = await get(sessionsRef);
      const sessions = snapshot.val() || {};
      const now = Date.now();
      const staleTimeout = 24 * 60 * 60 * 1000; // 24 hours
      
      for (const [key, session] of Object.entries(sessions)) {
        if (session.timestamp && (now - session.timestamp) > staleTimeout) {
          await set(ref(db, `sessions/${key}`), null);
        }
      }
    } catch (err) {
      console.error("Error clearing stale sessions:", err);
    }
  };

  const getClientInfo = useCallback(async () => {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      const userAgent = navigator.userAgent;
      return {
        ip: data.ip,
        userAgent: userAgent,
        timestamp: Date.now()
      };
    } catch (error) {
      return {
        ip: "unknown",
        userAgent: navigator.userAgent,
        timestamp: Date.now()
      };
    }
  }, []);

  const logLoginAttempt = async (username, success, reason = "") => {
    try {
      const clientInfo = await getClientInfo();
      const logRef = ref(db, `logs/login_attempts/${Date.now()}`);
      await set(logRef, {
        username,
        success,
        reason,
        timestamp: Date.now(),
        ...clientInfo
      });
    } catch (err) {
      console.error("Error logging attempt:", err);
    }
  };

  const checkLockout = (usernameKey) => {
    const attempts = loginAttemptsRef.current[usernameKey] || { count: 0, firstAttempt: Date.now() };
    const now = Date.now();
    
    if (attempts.count >= MAX_LOGIN_ATTEMPTS) {
      if (now - attempts.firstAttempt < LOCKOUT_TIME) {
        const remaining = LOCKOUT_TIME - (now - attempts.firstAttempt);
        setIsLocked(true);
        setLockoutTimeLeft(remaining);
        return true;
      } else {
        // Reset attempts after lockout period
        delete loginAttemptsRef.current[usernameKey];
        return false;
      }
    }
    return false;
  };

  const recordFailedAttempt = (usernameKey) => {
    const attempts = loginAttemptsRef.current[usernameKey] || { count: 0, firstAttempt: Date.now() };
    attempts.count++;
    loginAttemptsRef.current[usernameKey] = attempts;
    setFailedAttempts(attempts.count);
  };

  const resetFailedAttempts = (usernameKey) => {
    delete loginAttemptsRef.current[usernameKey];
    setFailedAttempts(0);
  };

  async function handleSubmit(e) {
    e.preventDefault();
    setMsg("");
    setMsgType("");
    
    const trimmedUsername = username.trim();
    const trimmedPassword = password.trim();
    
    if (!trimmedUsername || !trimmedPassword) {
      setMsg("⚠️ Please enter both username and password.");
      setMsgType("warning");
      return;
    }

    // Check lockout
    const usernameKey = trimmedUsername.toLowerCase();
    if (checkLockout(usernameKey)) {
      const minutes = Math.ceil(lockoutTimeLeft / 60000);
      setMsg(`⏰ Too many failed attempts. Please try again in ${minutes} minute${minutes > 1 ? 's' : ''}.`);
      setMsgType("error");
      return;
    }

    setBusy(true);

    try {
      // 🔹 Master Admin Login
      if (trimmedUsername === MASTER_USER && trimmedPassword === MASTER_PASS) {
        // Clear any previous sessions
        await set(ref(db, "sessions/admin"), {
          loggedIn: true,
          username: MASTER_USER,
          role: "admin",
          timestamp: Date.now(),
          lastActive: Date.now(),
          ...await getClientInfo()
        });
        
        // Save credentials if remember me is checked
        if (rememberMe) {
          localStorage.setItem("savedUsername", trimmedUsername);
          localStorage.setItem("savedPassword", trimmedPassword);
          localStorage.setItem("rememberMe", "true");
          localStorage.setItem("userRole", "admin");
        } else {
          localStorage.removeItem("savedUsername");
          localStorage.removeItem("savedPassword");
          localStorage.removeItem("rememberMe");
          localStorage.removeItem("userRole");
        }
        
        await logLoginAttempt(trimmedUsername, true, "Admin login");
        resetFailedAttempts(usernameKey);
        
        setMsg("✅ Admin login successful! Redirecting...");
        setMsgType("success");
        
        setTimeout(() => {
          navigate("/admin", { replace: true });
        }, 800);
        return;
      }

      // 🔹 Normal Area User Login with optimized query
      const areasRef = ref(db, "areas");
      const areasSnap = await get(areasRef);
      
      if (!areasSnap.exists()) {
        setMsg("❌ No areas found. Please contact administrator.");
        setMsgType("error");
        await logLoginAttempt(trimmedUsername, false, "No areas found");
        setBusy(false);
        return;
      }

      const areas = areasSnap.val();
      let foundArea = null;
      
      // Search for area by username, slug, or name
      for (const [id, data] of Object.entries(areas)) {
        const meta = data.meta || {};
        const areaUsername = (meta.username || "").toLowerCase();
        const areaSlug = (meta.slug || "").toLowerCase();
        const areaName = (meta.name || "").toLowerCase();
        const inputLower = trimmedUsername.toLowerCase();
        
        if (areaUsername === inputLower || areaSlug === inputLower || areaName === inputLower) {
          foundArea = { id, meta };
          break;
        }
      }

      if (!foundArea) {
        setMsg("❌ Area not found. Please check your username.");
        setMsgType("error");
        await logLoginAttempt(trimmedUsername, false, "Area not found");
        recordFailedAttempt(usernameKey);
        setBusy(false);
        return;
      }

      // Check password
      if (String(foundArea.meta.password) !== trimmedPassword) {
        setMsg("❌ Incorrect password. Please try again.");
        setMsgType("error");
        await logLoginAttempt(trimmedUsername, false, "Incorrect password");
        recordFailedAttempt(usernameKey);
        
        const remainingAttempts = MAX_LOGIN_ATTEMPTS - (loginAttemptsRef.current[usernameKey]?.count || 0);
        if (remainingAttempts > 0) {
          setMsg(`❌ Incorrect password. ${remainingAttempts} attempt${remainingAttempts > 1 ? 's' : ''} remaining.`);
        }
        setBusy(false);
        return;
      }

      // Check if area is active
      if (foundArea.meta.status === "inactive") {
        setMsg("⚠️ This area account is inactive. Please contact administrator.");
        setMsgType("warning");
        await logLoginAttempt(trimmedUsername, false, "Account inactive");
        setBusy(false);
        return;
      }

      // ✅ Login Success
      const clientInfo = await getClientInfo();
      const sessionData = {
        loggedIn: true,
        areaId: foundArea.id,
        username: foundArea.meta.username,
        slug: foundArea.meta.slug,
        name: foundArea.meta.name,
        timestamp: Date.now(),
        lastActive: Date.now(),
        ...clientInfo
      };
      
      // Store session in Firebase
      await set(ref(db, `sessions/areas/${foundArea.id}`), sessionData);
      
      // Update last login timestamp
      await update(ref(db, `areas/${foundArea.id}/meta`), {
        lastLogin: Date.now(),
        lastLoginIP: clientInfo.ip
      });

      // Save credentials if remember me is checked
      if (rememberMe) {
        localStorage.setItem("savedUsername", trimmedUsername);
        localStorage.setItem("savedPassword", trimmedPassword);
        localStorage.setItem("rememberMe", "true");
        localStorage.setItem("lastArea", foundArea.id);
        localStorage.setItem("lastAreaSlug", foundArea.meta.slug);
      } else {
        localStorage.removeItem("savedUsername");
        localStorage.removeItem("savedPassword");
        localStorage.removeItem("rememberMe");
        localStorage.removeItem("lastArea");
        localStorage.removeItem("lastAreaSlug");
      }

      await logLoginAttempt(trimmedUsername, true, `Area login: ${foundArea.meta.name}`);
      resetFailedAttempts(usernameKey);
      
      setMsg(`✅ Welcome ${foundArea.meta.name || foundArea.meta.username}! Redirecting...`);
      setMsgType("success");
      
      // Check for redirect after login
      const redirectPath = sessionStorage.getItem("redirectAfterLogin");
      if (redirectPath) {
        sessionStorage.removeItem("redirectAfterLogin");
        setTimeout(() => {
          navigate(redirectPath, { replace: true });
        }, 800);
      } else {
        setTimeout(() => {
          navigate(`/area/${foundArea.meta.slug}`, { replace: true });
        }, 800);
      }
      
    } catch (err) {
      console.error("Login Error Details:", err);
      setMsg("❌ Network error. Please check your connection and try again.");
      setMsgType("error");
      await logLoginAttempt(trimmedUsername, false, `Error: ${err.message}`);
    } finally {
      setBusy(false);
    }
  }

  const handleClear = () => {
    setUsername("");
    setPassword("");
    setRememberMe(false);
    setMsg("");
    setMsgType("");
    localStorage.removeItem("savedUsername");
    localStorage.removeItem("savedPassword");
    localStorage.removeItem("rememberMe");
    localStorage.removeItem("lastArea");
    localStorage.removeItem("lastAreaSlug");
    localStorage.removeItem("userRole");
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !busy && !isLocked) {
      handleSubmit(e);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <div className="logo">
            <span className="logo-icon"></span>
            <h1 className="brand">Jauhari DR</h1>
          </div>
          <p className="subtitle">Daily Report System</p>
        </div>

        <form className="login-form" onSubmit={handleSubmit} onKeyPress={handleKeyPress}>
          <div className="input-group">
            <label htmlFor="username">Username / Area Code</label>
            <div className="input-icon">
              <span className="icon">👤</span>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="login-input"
                placeholder="Enter username or area code"
                autoComplete="username"
                disabled={busy || isLocked}
                autoFocus
              />
            </div>
          </div>

          <div className="input-group">
            <label htmlFor="password">Password</label>
            <div className="input-icon">
              <span className="icon">🔒</span>
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="login-input"
                placeholder="Enter password"
                autoComplete="current-password"
                disabled={busy || isLocked}
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex="-1"
                disabled={busy || isLocked}
              >
                {showPassword ? "🙈" : "👁️"}
              </button>
            </div>
          </div>

          <div className="checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                disabled={busy || isLocked}
              />
              <span>Remember me</span>
            </label>
            <button type="button" onClick={handleClear} className="clear-btn" disabled={busy || isLocked}>
              Clear
            </button>
          </div>

          {failedAttempts > 0 && !isLocked && (
            <div className="attempts-warning">
              ⚠️ {MAX_LOGIN_ATTEMPTS - failedAttempts} login attempts remaining
            </div>
          )}

          {isLocked && (
            <div className="lockout-warning">
              ⏰ Account temporarily locked. Try again in {Math.ceil(lockoutTimeLeft / 60000)} minute{Math.ceil(lockoutTimeLeft / 60000) > 1 ? 's' : ''}
            </div>
          )}

          {msg && (
            <div className={`message ${msgType}`}>
              {msg}
            </div>
          )}

          <button 
            className="login-btn" 
            type="submit" 
            disabled={busy || isLocked}
          >
            {busy ? (
              <>
                <span className="spinner"></span>
                Authenticating...
              </>
            ) : (
              "Login →"
            )}
          </button>
        </form>

        <div className="login-footer">
          <p className="help-text">
            💡 Need help? Contact your system administrator
          </p>
          <p className="help-text">
            🔒 Secure login • All activities are logged
          </p>
        </div>
      </div>
      
      <footer className="login-footer-bottom">
        <p>© 2026 Jauhari WebTech — All Rights Reserved. v2.0</p>
      </footer>

      <style jsx>{`
        .login-container {
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          padding: 20px;
          position: relative;
        }

        .login-card {
          background: white;
          border-radius: 24px;
          padding: 40px;
          width: 100%;
          max-width: 450px;
          box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25);
          animation: slideUp 0.5s ease-out;
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .login-header {
          text-align: center;
          margin-bottom: 35px;
        }

        .logo {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          margin-bottom: 10px;
        }

        .logo-icon {
          font-size: 44px;
        }

        .brand {
          font-size: 28px;
          font-weight: 700;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          margin: 0;
          letter-spacing: -0.5px;
        }

        .subtitle {
          color: #666;
          font-size: 14px;
          margin-top: 5px;
        }

        .login-form {
          display: flex;
          flex-direction: column;
          gap: 22px;
        }

        .input-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .input-group label {
          font-size: 14px;
          font-weight: 600;
          color: #333;
        }

        .input-icon {
          position: relative;
          display: flex;
          align-items: center;
        }

        .icon {
          position: absolute;
          left: 14px;
          font-size: 18px;
          pointer-events: none;
        }

        .login-input {
          width: 100%;
          padding: 14px 14px 14px 45px;
          border: 2px solid #e5e7eb;
          border-radius: 12px;
          font-size: 15px;
          transition: all 0.3s;
          background: white;
        }

        .login-input:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102,126,234,0.1);
        }

        .login-input:disabled {
          background: #f9fafb;
          cursor: not-allowed;
          color: #9ca3af;
        }

        .toggle-password {
          position: absolute;
          right: 14px;
          background: none;
          border: none;
          cursor: pointer;
          font-size: 18px;
          padding: 0;
          transition: opacity 0.3s;
        }

        .toggle-password:hover:not(:disabled) {
          opacity: 0.7;
        }

        .toggle-password:disabled {
          cursor: not-allowed;
          opacity: 0.5;
        }

        .checkbox-group {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 5px;
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 10px;
          cursor: pointer;
          font-size: 14px;
          color: #666;
        }

        .checkbox-label input {
          width: 18px;
          height: 18px;
          cursor: pointer;
        }

        .clear-btn {
          background: none;
          border: none;
          color: #9ca3af;
          cursor: pointer;
          font-size: 14px;
          transition: all 0.3s;
          padding: 5px 10px;
          border-radius: 6px;
        }

        .clear-btn:hover:not(:disabled) {
          color: #667eea;
          background: #f3f4f6;
        }

        .clear-btn:disabled {
          cursor: not-allowed;
          opacity: 0.5;
        }

        .attempts-warning, .lockout-warning {
          padding: 10px;
          border-radius: 10px;
          font-size: 13px;
          text-align: center;
          background: #fff3cd;
          color: #856404;
          border: 1px solid #ffeeba;
        }

        .lockout-warning {
          background: #f8d7da;
          color: #721c24;
          border-color: #f5c6cb;
        }

        .message {
          padding: 12px;
          border-radius: 10px;
          font-size: 14px;
          text-align: center;
          animation: shake 0.5s;
          font-weight: 500;
        }

        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }

        .message.success {
          background: #d1fae5;
          color: #065f46;
          border: 1px solid #a7f3d0;
        }

        .message.error {
          background: #fee2e2;
          color: #991b1b;
          border: 1px solid #fecaca;
        }

        .message.warning {
          background: #fed7aa;
          color: #92400e;
          border: 1px solid #fed7aa;
        }

        .login-btn {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 14px;
          border: none;
          border-radius: 12px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          margin-top: 5px;
        }

        .login-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 10px 20px -5px rgba(102,126,234,0.4);
        }

        .login-btn:active:not(:disabled) {
          transform: translateY(0);
        }

        .login-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .spinner {
          width: 18px;
          height: 18px;
          border: 2px solid white;
          border-top: 2px solid transparent;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .login-footer {
          margin-top: 25px;
          text-align: center;
          padding-top: 20px;
          border-top: 1px solid #e5e7eb;
        }

        .help-text {
          font-size: 12px;
          color: #9ca3af;
          margin: 5px 0;
        }

        .login-footer-bottom {
          margin-top: 30px;
          text-align: center;
          color: rgba(255,255,255,0.8);
          font-size: 12px;
        }

        /* Mobile Responsive */
        @media (max-width: 480px) {
          .login-card {
            padding: 30px 20px;
          }
          
          .brand {
            font-size: 24px;
          }
          
          .logo-icon {
            font-size: 36px;
          }
          
          .login-input {
            padding: 12px 12px 12px 42px;
            font-size: 14px;
          }
          
          .login-btn {
            padding: 12px;
            font-size: 15px;
          }
        }
      `}</style>
    </div>
  );
}